import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import clsx from 'clsx';
import IconSvg from 'src/assets/icons/IconSvg';
import { RuneToken } from 'src/hooks/useRunes';

interface Props {
  runes: RuneToken[];
  selected: RuneToken | null;
  onSelect: (rune: RuneToken) => void;
  isLoading?: boolean;
  className?: string;
}

const RunesSelectionInput = ({ runes, selected, onSelect, isLoading, className }: Props) => {
  if (isLoading) {
    return (
      <div
        className={clsx(
          'flex items-center p-3 w-full rounded-lg bg-brand-black h-[48px]',
          className,
        )}
      >
        <span className="text-grayscale-500 text-sm">Loading runes...</span>
      </div>
    );
  }

  if (runes.length === 0) {
    return (
      <div
        className={clsx(
          'flex items-center p-3 w-full rounded-lg bg-brand-black h-[48px]',
          className,
        )}
      >
        <span className="text-grayscale-500 text-sm">No runes in wallet</span>
      </div>
    );
  }

  return (
    <Listbox value={selected} onChange={onSelect}>
      <ListboxButton
        className={clsx(
          'flex justify-between items-center p-3 w-full rounded-lg bg-brand-black h-[48px]',
          className,
        )}
      >
        <div className="flex gap-2 items-center min-w-0">
          {selected?.imageUrl ? (
            <img
              src={selected.imageUrl}
              alt={selected.name}
              className="w-6 h-6 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-6 h-6 bg-grayscale-600 rounded-full flex-shrink-0" />
          )}

          <span className="text-brand-magnolia truncate">
            {selected?.name || 'Select a Rune'}
          </span>
        </div>

        <IconSvg icon="arrowDownS" className="text-grayscale-300 flex-shrink-0" />
      </ListboxButton>

      <ListboxOptions
        anchor="bottom"
        transition
        className={clsx(
          'rounded-lg cursor-pointer w-[var(--button-width)] bg-brand-black [--anchor-gap:5px] z-50',
          'transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0',
        )}
      >
        {runes.map((rune) => (
          <ListboxOption
            key={rune.id}
            value={rune}
            className="flex gap-2 items-center p-3 hover:bg-grayscale-800"
          >
            {rune.imageUrl ? (
              <img
                src={rune.imageUrl}
                alt={rune.name}
                className="w-6 h-6 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-6 h-6 bg-grayscale-600 rounded-full flex-shrink-0" />
            )}

            <span className="text-brand-magnolia truncate">{rune.name}</span>
          </ListboxOption>
        ))}
      </ListboxOptions>
    </Listbox>
  );
};

export default RunesSelectionInput;
